const KEYS = {
  token: 'aurabook_token',
  user: 'aurabook_user',
  adminToken: 'aurabook_admin_token',
  adminUser: 'aurabook_admin_user',
  impersonatingSalon: 'aurabook_impersonating_salon',
};

const LEGACY = {
  token: 'glowos_token',
  user: 'glowos_user',
  adminToken: 'glowos_admin_token',
  adminUser: 'glowos_admin_user',
  impersonatingSalon: 'glowos_impersonating_salon',
};

/** Preferences kept across logout (not session/cache). */
const PREFERENCE_KEYS = new Set(['aurabook_theme', 'glowos_theme', 'glowos_lang', 'aurabook_lang']);

const APP_PREFIXES = ['aurabook_', 'glowos_'];
const EXTRA_CACHE_KEYS = ['glowos_cart', 'aurabook_cart'];

const read = (key, legacyKey) => {
  const value = localStorage.getItem(key);
  if (value != null) return value;
  const legacy = localStorage.getItem(legacyKey);
  if (legacy != null) {
    localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
  }
  return legacy;
};

const write = (key, legacyKey, value) => {
  localStorage.setItem(key, value);
  localStorage.removeItem(legacyKey);
};

const remove = (key, legacyKey) => {
  localStorage.removeItem(key);
  localStorage.removeItem(legacyKey);
};

const removeMatchingLocalStorage = () => {
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || PREFERENCE_KEYS.has(key)) continue;
    if (APP_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
  EXTRA_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const clearSession = () => {
  Object.keys(KEYS).forEach((k) => remove(KEYS[k], LEGACY[k]));
};

/**
 * Clears auth + app caches. Theme/language preferences are kept.
 * Browser HTTP disk cache is also busted indirectly via no-store API headers
 * and hard redirects after login/logout.
 */
const clearClientCache = async () => {
  clearSession();
  removeMatchingLocalStorage();
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }

  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch {
      /* ignore */
    }
  }

  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
    } catch {
      /* ignore */
    }
  }
};

export const storage = {
  getToken: () => read(KEYS.token, LEGACY.token),
  setToken: (v) => write(KEYS.token, LEGACY.token, v),
  getUser: () => read(KEYS.user, LEGACY.user),
  setUser: (v) => write(KEYS.user, LEGACY.user, v),
  getAdminToken: () => read(KEYS.adminToken, LEGACY.adminToken),
  setAdminToken: (v) => write(KEYS.adminToken, LEGACY.adminToken, v),
  getAdminUser: () => read(KEYS.adminUser, LEGACY.adminUser),
  setAdminUser: (v) => write(KEYS.adminUser, LEGACY.adminUser, v),
  getImpersonatingSalon: () => read(KEYS.impersonatingSalon, LEGACY.impersonatingSalon),
  setImpersonatingSalon: (v) => write(KEYS.impersonatingSalon, LEGACY.impersonatingSalon, v),
  clearSession,
  clearClientCache,
};
